import {Rule} from '../../models/svg-rule/rule.model';
import {IRuleSystem} from '../../models/svg-rule/rule-system.model';
import {Color} from '../../enums/color';
import {TcddTrainTabUtil, ValueIndex} from '../../utils/tcdd-train-tab-util';
import {TcddMainSystem} from '../../enums/tcdd-main-system.enum';
import {TcddSubSystem} from '../../enums/tcdd-sub-system.enum';
import {TcddSubSystemType} from '../../enums/tcdd-sub-system-type.enum';
import {BrakeSystemKeys} from '../../constants/tcdd-system-keys/brake-system-keys';
import {BRAKE_SYSTEM} from '../../constants/tcdd-system-contants/brake-system.constant';
import {TCDD_UNIT} from '../../constants/tcdd-unit-constant';
import {createColorRule, createTextRule} from '../../utils/tcdd-rule-util';

function getIndex(key: string): ValueIndex {
  return TcddTrainTabUtil.getValueIndex(
    TcddMainSystem.DIAGNOSTIC_SYSTEM,
    TcddSubSystem.BRAKE_SYSTEM,
    TcddSubSystemType.SUB,
    key
  );
}

export class BrakeSystemRules implements IRuleSystem {
  public getRules(): Rule[] {
    const evaluateParameters = (
      wagonIndex: number,
      data: any[],
      suffix: 'TB' | 'MB'
    ): Color => {
      const wagonData = data?.[TcddSubSystem.BRAKE_SYSTEM]?.[wagonIndex];
      if (!wagonData || !suffix) {
        return null;
      }

      const diaCPressHoseBrake: ValueIndex = getIndex(`${BrakeSystemKeys.diaCPressHoseBrake}${suffix}`); // === 1 will be red
      const diaBrakeNotReleased: ValueIndex = getIndex(`${BrakeSystemKeys.diaBrakeNotReleased}${suffix}`); // === 1 will be red
      const diaBrakeNotApplied: ValueIndex = getIndex(`${BrakeSystemKeys.diaBrakeNotApplied}${suffix}`); // === 1 will be red
      const diaDirectBrakeDist: ValueIndex = getIndex(`${BrakeSystemKeys.diaDirectBrakeDist}${suffix}`); // === 1 will be red
      const pressureGovernorPressureAirSuspension: ValueIndex = getIndex(`${BrakeSystemKeys.pressureGovernorPressureAirSuspension}${suffix}`); // === 0 will be red
      const diaAirSuspPressInvalid: ValueIndex = getIndex(`${BrakeSystemKeys.diaAirSuspPressInvalid}${suffix}`); // === 1 will be red
      const diaWspFailure: ValueIndex = getIndex(`${BrakeSystemKeys.diaWspFailure}${suffix}`); // === 1 will be red
      const pressureGovernorBrakeUnitAppliedReleased: ValueIndex = getIndex(`${BrakeSystemKeys.pressureGovernorBrakeUnitAppliedReleased}${suffix}`); // === 1 will be gray

      if ( // Red state
        Number(wagonData[diaCPressHoseBrake.groupIndex]?.[diaCPressHoseBrake.infoIndex]) === BRAKE_SYSTEM.STATUS.C_PRESS_HOSE_BRAKE ||
        Number(wagonData[diaBrakeNotReleased.groupIndex]?.[diaBrakeNotReleased.infoIndex]) === BRAKE_SYSTEM.STATUS.BRAKE_NOT_RELEASED ||
        Number(wagonData[diaBrakeNotApplied.groupIndex]?.[diaBrakeNotApplied.infoIndex]) === BRAKE_SYSTEM.STATUS.BRAKE_NOT_APPLIED ||
        Number(wagonData[diaDirectBrakeDist.groupIndex]?.[diaDirectBrakeDist.infoIndex]) === BRAKE_SYSTEM.STATUS.DIRECT_BRAKE_DIST ||
        Number(wagonData[pressureGovernorPressureAirSuspension.groupIndex]?.[pressureGovernorPressureAirSuspension.infoIndex]) === BRAKE_SYSTEM.STATUS.AIR_SUSP_PRESS_FAULT ||
        Number(wagonData[diaAirSuspPressInvalid.groupIndex]?.[diaAirSuspPressInvalid.infoIndex]) === BRAKE_SYSTEM.STATUS.AIR_SUSP_PRESS_INVALID ||
        Number(wagonData[diaWspFailure.groupIndex]?.[diaWspFailure.infoIndex]) === BRAKE_SYSTEM.STATUS.WSP_FAILURE
      ) {
        return Color.Red;
      }

      if (Number(wagonData[pressureGovernorBrakeUnitAppliedReleased.groupIndex]?.[pressureGovernorBrakeUnitAppliedReleased.infoIndex]) === BRAKE_SYSTEM.STATUS.BRAKE_UNIT_APPLIED_RELEASED) { // Gray state
        return Color.Gray;
      }

      return null;
    };

    return [
      createColorRule(TCDD_UNIT.BRAKE_TB, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const resultColor: Color = evaluateParameters(wagonIndex, data, 'TB');
        setColor(resultColor);
        return !!resultColor;
      }),
      createColorRule(TCDD_UNIT.BRAKE_MB, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const resultColor: Color = evaluateParameters(wagonIndex, data, 'MB');
        setColor(resultColor);
        return !!resultColor;
      }),
      createTextRule(
        TCDD_UNIT.TEXT_BRAKE_TB,
        (wagonIndex, data, setColor, setText) => {
          const textValue = data?.[TcddSubSystem.TEXT_SYSTEM]?.[TcddSubSystem.BRAKE_SYSTEM]?.[wagonIndex]?.[BrakeSystemKeys.brakeTB] ?? '-';
          setText(`${textValue} bar`);
          setColor(Color.White);
          return true;
        },
        {
          fontSize: '16',
          fontFamily: 'Roboto',
          fontStyle: 'normal',
          fontWeight: '500',
          offsetX: 5,
          offsetY: 0,
          position: 'right',
          textAnchor: 'start'
        }
      ),
      createTextRule(
        TCDD_UNIT.TEXT_BRAKE_MB,
        (wagonIndex, data, setColor, setText) => {
          const textValue = data?.[TcddSubSystem.TEXT_SYSTEM]?.[TcddSubSystem.BRAKE_SYSTEM]?.[wagonIndex]?.[BrakeSystemKeys.brakeMB] ?? '-';
          setText(`${textValue} bar`);
          setColor(Color.White);
          return true;
        },
        {
          fontSize: '16',
          fontFamily: 'Roboto',
          fontStyle: 'normal',
          fontWeight: '500',
          offsetX: 5,
          offsetY: 0,
          position: 'right',
          textAnchor: 'start'
        }
      )
    ];
  }
}
